import { useQuery } from "@tanstack/react-query";
import {
  type LoaderFunction,
  Navigate,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { getPlatformUserMemberships } from "api/methods";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";

const load = async (platformUserMembershipId: string) => {
  const userMemberships = queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });

  return waitObj({
    platformUserMembershipId,
    userMemberships,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipCancelFreezeOfferLoader: LoaderFunction = ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("..");
  }

  return load(platformUserMembershipId);
};

/**
 * Save-offer interstitial shown between the cancellation-reason page and the
 * final cancel confirmation when the membership is freeze-eligible. Purely
 * presentational: it either forwards the chosen reason to the confirm page
 * unchanged or diverts to the existing freeze request page.
 */
export const WalletMembershipCancelFreezeOfferPage = () => {
  const location = useLocation();

  const { platformUserMembershipId, userMemberships: initialUserMemberships } =
    useLoaderData() as LoaderData;

  const navigate = useNavigate();

  const { data: userMemberships } = useQuery({
    ...getPlatformUserMemberships.keyed(),
    initialData: initialUserMemberships,
  });
  const membership = userMemberships.find(
    (m) => m.platformUserMembershipId === platformUserMembershipId,
  );

  const membershipCancellationReasonId: string = location.state?.membershipCancellationReasonId;

  if (!membershipCancellationReasonId) {
    return <Navigate replace to={`/wallet/memberships/manage/${platformUserMembershipId}`} />;
  }

  return (
    <div className="relative flex h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">CANCEL MY MEMBERSHIP</div>
      </TopNav>
      <div className="flex flex-col gap-y-6 bg-white px-7 pb-5 pt-3 text-left">
        <h1 className="text-title">Before you go — need a break instead?</h1>
        <div className="flex flex-col rounded-lg bg-white p-4 shadow-[0px_2px_10px_rgba(0,0,0,0.06)]">
          <div className="text-body2 text-secondary">
            Freeze your {membership?.membership.name} membership instead of canceling. You
            won&apos;t be billed while it&apos;s frozen, and it resumes automatically when the
            freeze ends — so you keep your membership without canceling.
          </div>
        </div>
      </div>
      <div className="grow" />
      <div className="flex flex-col gap-y-3 px-7 pb-5">
        <Button
          fullWidth
          style="primary"
          onClick={() => navigate(`/wallet/memberships/manage/${platformUserMembershipId}/freeze`)}
        >
          Freeze my membership instead
        </Button>
        <Button
          fullWidth
          style="secondary"
          onClick={() =>
            navigate(`/wallet/memberships/manage/${platformUserMembershipId}/cancel`, {
              state: {
                membershipCancellationReasonId,
              },
            })
          }
        >
          Continue to cancel
        </Button>
      </div>
    </div>
  );
};
